import { Link } from '@tanstack/react-router';
import { Badge, Card, CardTitle, EmptyState, type BadgeTone } from '@wundo/ui';
import type { SkillRecentRecommendation } from '@wundo/shared/skillStats.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';

const DATE_TIME: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

const STATUS_TONES: Record<string, BadgeTone> = {
  pending: 'warning',
  delivered: 'success',
  skipped: 'neutral',
  superseded: 'info',
  expired: 'danger',
};

/**
 * The last times this skill was scored into somebody's outreach, and what
 * became of each one. A row opens the run it came out of.
 */
export function SkillRecentOutreaches({
  recent,
}: {
  recent: SkillRecentRecommendation[];
}) {
  return (
    <Card as='section' aria-label='Recent outreaches'>
      <CardTitle as='h3'>Recent outreaches ({recent.length})</CardTitle>

      {recent.length === 0 ? (
        <EmptyState title='No outreaches yet' />
      ) : (
        <ol className='mt-3.5 flex flex-col gap-2'>
          {recent.map((rec) => (
            <li key={rec.id}>
              <Link
                to='/admin/recommendation-runs/$runId/users/$userId'
                params={{ runId: rec.run_id, userId: rec.user_id }}
                className='block rounded-lg border border-line p-2 hover:bg-fill-faint'
              >
                <div className='flex items-center justify-between gap-2'>
                  <span className='truncate text-sm font-semibold text-ink'>
                    {rec.user_name ?? rec.user_id.slice(0, 8)}
                  </span>
                  <div className='flex shrink-0 items-center gap-2'>
                    <span className='font-mono text-xs text-ink-secondary'>
                      {rec.final_score.toFixed(1)}
                    </span>
                    <Badge tone={STATUS_TONES[rec.status] ?? 'neutral'}>
                      {rec.status}
                    </Badge>
                  </div>
                </div>
                <p className='mt-1 truncate text-xs text-ink-muted'>
                  {rec.skipped_reason ?? rec.reason}
                </p>
                <p className='mt-0.5 text-xs text-ink-muted'>
                  {formatDateTime(rec.recommended_at, DATE_TIME)}
                </p>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
